import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { spaceApi, type MissionType, type RelationActionType, type TechnologyActionType } from '../api/space';
import { useToastStore } from '../stores/toastStore';

export function useSpaceOverview() {
  return useQuery({
    queryKey: ['space', 'overview'],
    queryFn: () => spaceApi.getOverview().then((res) => res.data.data),
    staleTime: 20000,
  });
}

function invalidateSpace(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['space', 'overview'] });
  queryClient.invalidateQueries({ queryKey: ['stars', 'map'] });
  queryClient.invalidateQueries({ queryKey: ['planet', 'me'] });
  queryClient.invalidateQueries({ queryKey: ['credits', 'transactions'] });
}

export function useRunSpaceAction() {
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);
  return useMutation({
    mutationFn: ({ actionType }: { actionType: TechnologyActionType }) =>
      spaceApi.runAction(actionType).then((res) => res.data.data),
    onSuccess: () => {
      invalidateSpace(queryClient);
      addToast('Space operation started.', 'success');
    },
    onError: (error: any) => {
      addToast(error.response?.data?.error?.message || 'Technology action failed', 'error');
    },
  });
}

export function useBroadcastSignal() {
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);
  return useMutation({
    mutationFn: (input: { signalType?: string; directionStarIndex?: number; message?: string }) =>
      spaceApi.broadcastSignal(input).then((res) => res.data.data),
    onSuccess: () => {
      invalidateSpace(queryClient);
      addToast('Signal broadcast launched.', 'success');
    },
    onError: (error: any) => {
      addToast(error.response?.data?.error?.message || 'Signal failed', 'error');
    },
  });
}

export function useCreateSpaceMission() {
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);
  return useMutation({
    mutationFn: (input: { missionType: MissionType; targetPlanetId?: string; targetStarIndex?: number }) =>
      spaceApi.createMission(input).then((res) => res.data.data),
    onSuccess: () => {
      invalidateSpace(queryClient);
      addToast('Space mission launched.', 'success');
    },
    onError: (error: any) => {
      addToast(error.response?.data?.error?.message || 'Mission failed', 'error');
    },
  });
}

export function useRelationAction() {
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);
  return useMutation({
    mutationFn: ({ planetId, action }: { planetId: string; action: RelationActionType }) =>
      spaceApi.relationAction(planetId, action).then((res) => res.data.data),
    onSuccess: () => {
      invalidateSpace(queryClient);
      addToast('Relation updated.', 'success');
    },
    onError: (error: any) => {
      addToast(error.response?.data?.error?.message || 'Relation action failed', 'error');
    },
  });
}
